/**
 * Tela de Relatórios — visão gerencial para admin e diretor.
 *
 * Permite gerar e exportar relatórios pré-prontos (propostas por período, por vendedor, por status,
 * receita projetada, propostas vencendo) com filtro por calendário e exportação em CSV e PDF.
 *
 * Permissão: `ver_auditoria` (admin e diretor). Vendedor e leitor não veem esta aba.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { listarPropostas } from '../api/cliente.ts';
import { useAutenticacao } from '../autenticacao/contexto.tsx';
import {
  calcularIntervalo,
  descreverPeriodo,
  filtrarPorIntervalo,
  formatarDataIso,
  ROTULOS_PERIODO,
  trimestreCorrente,
  trimestresDisponiveis,
  rotuloTrimestre,
  type TipoPeriodo,
} from './periodoDashboard.ts';
import {
  gerarRelatorio,
  montarCsv,
  nomeArquivo,
  RELATORIOS,
  type DadosRelatorio,
  type ItemLista,
  type TipoRelatorio,
} from './relatorios.ts';
import { montarPdf } from './relatoriosPdf.ts';

// ---------------------------------------------------------------------------
// Download — a única parte da exportação que depende do navegador
// ---------------------------------------------------------------------------

/**
 * Entrega um arquivo ao usuário.
 *
 * Isolado porque é o trecho que só existe no navegador: `Blob`, âncora e `revokeObjectURL`. A montagem
 * do conteúdo fica em `relatorios.ts` e `relatoriosPdf.ts`, que rodam em teste.
 */
function baixar(conteudo: BlobPart, tipoMime: string, nome: string): void {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipoMime }));
  const a = document.createElement('a');

  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportarCsv(dados: DadosRelatorio, descricaoPeriodo: string): void {
  /*
   * O BOM é obrigatório. Sem ele o Excel em português abre o arquivo em cp1252 e todo acento do nome de
   * cliente vira caractere trocado — a planilha chega ao gestor ilegível.
   */
  const conteudo = `\uFEFF${montarCsv(dados, descricaoPeriodo, new Date())}`;
  const nome = nomeArquivo(dados, 'csv', formatarDataIso(new Date()));

  baixar(conteudo, 'text/csv;charset=utf-8;', nome);
}

async function exportarPdf(dados: DadosRelatorio, descricaoPeriodo: string): Promise<void> {
  const bytes = await montarPdf(dados, descricaoPeriodo, new Date());
  const nome = nomeArquivo(dados, 'pdf', formatarDataIso(new Date()));

  baixar(bytes, 'application/pdf', nome);
}

// ---------------------------------------------------------------------------
// Opções de período (mesmas do dashboard)
// ---------------------------------------------------------------------------

const OPCOES_PERIODO: readonly TipoPeriodo[] = [
  'hoje',
  'semanaAtual',
  'mesAtual',
  'trimestre',
  'anoAtual',
  'tudo',
  'personalizado',
];

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function TelaRelatorios() {
  const { sessao } = useAutenticacao();

  const [todosItens, setTodosItens] = useState<readonly ItemLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Seleção de relatório
  const [relatorioSelecionado, setRelatorioSelecionado] = useState<TipoRelatorio | null>(null);

  // Período
  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>('tudo');
  const [dataDe, setDataDe] = useState('');
  const [dataAte, setDataAte] = useState('');
  const [trimestreSelecionado, setTrimestreSelecionado] = useState('');

  // Estado da exportação PDF
  const [exportandoPdf, setExportandoPdf] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      const r = await listarPropostas();

      if (!r.ok) {
        const dados = r.dados as { mensagem?: string };
        setErro(
          dados.mensagem ?? `Não foi possível carregar as propostas (HTTP ${String(r.status)}).`,
        );
        return;
      }

      const dados = r.dados as { itens?: readonly ItemLista[] };
      setTodosItens(dados.itens ?? []);
    } catch (e) {
      setErro(
        `Falha na comunicação com o servidor: ${e instanceof Error ? e.message : 'erro desconhecido'}`,
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Intervalo calculado
  const intervalo = useMemo(
    () => calcularIntervalo(tipoPeriodo, dataDe, dataAte, new Date(), trimestreSelecionado),
    [tipoPeriodo, dataDe, dataAte, trimestreSelecionado],
  );

  // Itens filtrados
  const itensFiltrados = useMemo(
    () => filtrarPorIntervalo(todosItens, intervalo),
    [todosItens, intervalo],
  );

  // Descrição do período
  const descricaoPeriodo = useMemo(
    () => descreverPeriodo(tipoPeriodo, intervalo, new Date(), trimestreSelecionado),
    [tipoPeriodo, intervalo, trimestreSelecionado],
  );

  // Trimestres disponíveis
  const trimestres = useMemo(() => trimestresDisponiveis(todosItens, new Date()), [todosItens]);

  // Dados do relatório selecionado
  const dadosRelatorio = useMemo(() => {
    if (relatorioSelecionado === null) return null;
    return gerarRelatorio(relatorioSelecionado, itensFiltrados);
  }, [relatorioSelecionado, itensFiltrados]);

  // Data de hoje para limitar inputs
  const hojeIso = useMemo(() => formatarDataIso(new Date()), []);

  const handleExportarCsv = useCallback(() => {
    if (dadosRelatorio === null) return;
    exportarCsv(dadosRelatorio, descricaoPeriodo);
  }, [dadosRelatorio, descricaoPeriodo]);

  const handleExportarPdf = useCallback(async () => {
    if (dadosRelatorio === null) return;
    setExportandoPdf(true);
    try {
      await exportarPdf(dadosRelatorio, descricaoPeriodo);
    } finally {
      setExportandoPdf(false);
    }
  }, [dadosRelatorio, descricaoPeriodo]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-live="polite">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-3 border-gray-200 border-t-brand-accent" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (erro !== null) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-6 text-center">
        <p className="text-sm text-red-700 dark:text-red-400">{erro}</p>
        <button
          type="button"
          onClick={() => void carregar()}
          className="mt-4 rounded-lg bg-red-100 dark:bg-red-800 px-4 py-2 text-sm font-semibold text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatórios</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Relatórios gerenciais — {String(todosItens.length)} propostas no histórico
          </p>
        </div>
        {sessao !== null && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent/10 px-3 py-1 text-xs font-medium text-brand-accent">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-3.5 w-3.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
              />
            </svg>
            Visão gerencial
          </span>
        )}
      </div>

      {/* Filtro de período */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
          Filtrar período
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor="tipo-periodo"
              className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
            >
              Período
            </label>
            <select
              id="tipo-periodo"
              value={tipoPeriodo}
              onChange={(e) => {
                const novo = e.target.value as TipoPeriodo;
                setTipoPeriodo(novo);
                if (novo === 'trimestre' && trimestreSelecionado === '') {
                  setTrimestreSelecionado(trimestreCorrente(new Date()));
                }
              }}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
            >
              {OPCOES_PERIODO.map((op) => (
                <option key={op} value={op}>
                  {ROTULOS_PERIODO[op]}
                </option>
              ))}
            </select>
          </div>

          {tipoPeriodo === 'trimestre' && (
            <div>
              <label
                htmlFor="trimestre"
                className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
              >
                Trimestre
              </label>
              <select
                id="trimestre"
                value={trimestreSelecionado}
                onChange={(e) => {
                  setTrimestreSelecionado(e.target.value);
                }}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
              >
                {trimestres.map((t) => (
                  <option key={t} value={t}>
                    {rotuloTrimestre(t, new Date())}
                  </option>
                ))}
              </select>
            </div>
          )}

          {tipoPeriodo === 'personalizado' && (
            <>
              <div>
                <label
                  htmlFor="data-de"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                >
                  De
                </label>
                <input
                  id="data-de"
                  type="date"
                  value={dataDe}
                  max={hojeIso}
                  onChange={(e) => {
                    setDataDe(e.target.value);
                  }}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                />
              </div>
              <div>
                <label
                  htmlFor="data-ate"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                >
                  Até
                </label>
                <input
                  id="data-ate"
                  type="date"
                  value={dataAte}
                  max={hojeIso}
                  onChange={(e) => {
                    setDataAte(e.target.value);
                  }}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                />
              </div>
            </>
          )}

          <div className="text-xs text-gray-500 dark:text-gray-400 self-center">
            {itensFiltrados.length} de {todosItens.length} propostas no período selecionado
          </div>
        </div>
      </div>

      {/* Menu de relatórios */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {RELATORIOS.map((rel) => (
          <button
            key={rel.id}
            type="button"
            onClick={() => {
              setRelatorioSelecionado(rel.id);
            }}
            className={[
              'rounded-xl border p-4 text-left transition-all hover:shadow-md',
              relatorioSelecionado === rel.id
                ? 'border-brand-accent bg-brand-accent/5 dark:bg-brand-accent/10 ring-1 ring-brand-accent'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-brand-accent/50',
            ].join(' ')}
          >
            <span className="text-2xl">{rel.icone}</span>
            <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">{rel.nome}</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{rel.descricao}</p>
          </button>
        ))}
      </div>

      {/* Resultado do relatório */}
      {dadosRelatorio !== null && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          {/* Header do resultado */}
          <div className="flex flex-col gap-3 border-b border-gray-200 dark:border-gray-700 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {dadosRelatorio.titulo}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Período: {descricaoPeriodo}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleExportarCsv}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
                CSV
              </button>
              <button
                type="button"
                onClick={() => void handleExportarPdf()}
                disabled={exportandoPdf}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-accent px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90 disabled:opacity-50"
              >
                {exportandoPdf ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                    />
                  </svg>
                )}
                PDF
              </button>
            </div>
          </div>

          {/* Tabela */}
          {dadosRelatorio.linhas.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhum dado encontrado para o período selecionado.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                    {dadosRelatorio.cabecalho.map((col, i) => (
                      <th
                        key={i}
                        className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dadosRelatorio.linhas.map((linha, i) => (
                    <tr
                      key={i}
                      className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    >
                      {linha.colunas.map((col, j) => (
                        <td
                          key={j}
                          className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300"
                        >
                          {col}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Resumo */}
          {dadosRelatorio.resumo !== undefined && dadosRelatorio.linhas.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {dadosRelatorio.resumo}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Estado vazio — nenhum relatório selecionado */}
      {relatorioSelecionado === null && (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-12 text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
            />
          </svg>
          <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Selecione um relatório acima
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Escolha o tipo de relatório e ajuste o período para gerar a visualização
          </p>
        </div>
      )}
    </div>
  );
}
